import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { useAuth } from "../contexts/AuthContext";
import {
  IconRefresh, IconSendPlane, IconArrowUpRight, IconContacts, IconBlast,
  IconDelivered, IconReply, IconCampaigns, IconOptout,
} from "../components/Icons";

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: "today", label: "Today" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
  { key: "90d", label: "90D" },
  { key: "all", label: "All Time" },
];

/* ── Stat Card ── */
function StatCard({ label, value, sub, isPrimary, icon: Icon }) {
  return (
    <div
      className="flex items-start gap-3 px-5 py-4"
      style={{
        background: isPrimary
          ? "linear-gradient(135deg, #FF5C35 0%, #FF6E4B 100%)"
          : "#FFFFFF",
        border: isPrimary ? "none" : "1px solid #E6E6E6",
        borderRadius: "11px",
        boxShadow: "0 4px 4px rgba(0,0,0,0.03)",
        minHeight: "97px",
        minWidth: 0,
      }}
    >
      {Icon && (
        <div className="shrink-0 mt-1">
          <Icon size={22} color={isPrimary ? "rgba(255,255,255,0.9)" : "#555555"} />
        </div>
      )}
      <div className="flex flex-col justify-center min-w-0">
        <p
          className="text-2xl font-bold uppercase"
          style={{
            color: isPrimary ? "#FFFFFF" : "#555555",
            letterSpacing: "0.1em",
            fontFamily: "Montserrat, sans-serif",
            lineHeight: 1.2,
          }}
        >
          {value}
        </p>
        <p
          className="text-[11px] font-medium uppercase mt-1"
          style={{
            color: isPrimary ? "rgba(255,255,255,0.85)" : "#555555",
            letterSpacing: "0.1em",
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          {label}
        </p>
        {sub && (
          <p
            className="text-[11px] font-medium mt-0.5"
            style={{ color: isPrimary ? "rgba(255,255,255,0.7)" : "#466E3D", letterSpacing: "0.05em", fontFamily: "Montserrat, sans-serif" }}
          >
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

/* ── Quick Action Card ── */
function QuickAction({ to, title, desc, bgColor, icon: Icon }) {
  return (
    <Link
      to={to}
      className="relative flex flex-col justify-end p-5 transition-all duration-200 hover:scale-[1.02] group"
      style={{
        backgroundColor: bgColor,
        borderRadius: "11px",
        minHeight: "140px",
        overflow: "hidden",
      }}
    >
      {/* Main icon */}
      {Icon && (
        <div className="absolute top-4 left-5">
          <Icon size={20} color="rgba(255,255,255,0.85)" />
        </div>
      )}
      {/* Arrow */}
      <div
        className="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
      >
        <IconArrowUpRight size={12} color="white" />
      </div>
      <p
        className="text-base font-bold text-white uppercase"
        style={{ letterSpacing: "0.1em", fontFamily: "Montserrat, sans-serif", lineHeight: 1 }}
      >
        {title}
      </p>
      <p
        className="text-[10px] font-medium text-white mt-1.5"
        style={{ opacity: 0.75, fontFamily: "Montserrat, sans-serif" }}
      >
        {desc}
      </p>
    </Link>
  );
}

/* ── Custom Bar Shape (rounded, Figma style) ── */
function RoundedBar(props) {
  const { x, y, width, height } = props;
  if (!height || height <= 0) return null;
  const r = Math.min(8, height, width / 2);
  return (
    <rect x={x} y={y} width={width} height={height} rx={r} ry={r} fill="#F1F1F1" stroke="#E1E1E1" strokeWidth={1} />
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState("7d");

  const load = (range) => {
    const r = range || dateFilter;
    setLoading(true);
    axios.get(`${API}/api/analytics/dashboard?range=${r}`)
      .then(res => setStats(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleFilterChange = (key) => {
    setDateFilter(key);
    load(key);
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#FF5C35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const chartData = stats?.daily_stats?.map(d => ({
    date: d._id?.slice(5),
    sent: d.sent || 0,
    replies: d.replies || 0,
  })) || [];

  const firstName = user?.name?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  const rangeLabel = { today: "Today", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", all: "All time" }[dateFilter] || "Last 7 days";

  /* Shared button height style */
  const btnH = { height: "42px", fontSize: "14px", fontFamily: "Inter, sans-serif" };

  return (
    <div data-testid="dashboard-page">
      {/* ── Header Area ── */}
      <div
        className="flex items-center justify-between px-6 md:px-10 py-5"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div>
          <p className="text-xl" style={{ fontFamily: "Inter, sans-serif", color: "#000000" }}>
            Welcome back,
          </p>
          <p className="text-4xl font-bold" style={{ fontFamily: "Inter, sans-serif", color: "#FF5C35" }}>
            {firstName}
          </p>
          <p className="text-[10px] mt-0.5" style={{ fontFamily: "Inter, sans-serif", color: "#000000" }}>
            Here's what's happening with your SMS campaigns today
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-5 text-sm font-medium transition-all duration-200"
            style={{
              ...btnH,
              color: "#385063",
              border: "0.5px solid #B2CCE0",
              borderRadius: "11px",
              background: "#FFFFFF",
            }}
            data-testid="refresh-dashboard"
          >
            <IconRefresh size={15} color="#385063" />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <Link
            to="/blast"
            className="inline-flex items-center gap-2 px-5 text-white font-bold transition-all duration-200"
            style={{
              ...btnH,
              background: "linear-gradient(135deg, #FF5C35 0%, #FF7F5C 100%)",
              borderRadius: "11px",
            }}
            data-testid="new-blast-btn"
          >
            <IconSendPlane size={15} />
            <span className="hidden sm:inline">Send Blast</span>
          </Link>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-6 md:px-10 py-6 space-y-6">
        {/* Filter Pills */}
        <div className="flex items-center gap-2">
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => handleFilterChange(f.key)}
              className="transition-all duration-200"
              style={{
                padding: "6px 14px",
                borderRadius: "6px",
                fontSize: "11px",
                fontWeight: 500,
                fontFamily: "Montserrat, sans-serif",
                letterSpacing: "0.05em",
                backgroundColor: dateFilter === f.key ? "#FF5C35" : "#FFFFFF",
                color: dateFilter === f.key ? "#FFFFFF" : "#555555",
                border: dateFilter === f.key ? "none" : "1px solid #E6E6E6",
                cursor: "pointer",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Stat Cards Row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Contacts" value={stats?.total_contacts?.toLocaleString() || "0"} isPrimary icon={IconContacts} />
          <StatCard label="Messages Sent" value={stats?.total_sent?.toLocaleString() || "0"} icon={IconBlast} />
          <StatCard label="Delivered" value={stats?.total_delivered?.toLocaleString() || "0"} sub={`${stats?.delivery_rate || 0}%`} icon={IconDelivered} />
          <StatCard label="Replies" value={stats?.total_replies?.toLocaleString() || "0"} sub={`${stats?.reply_rate || 0}%`} icon={IconReply} />
          <StatCard label="Active Campaigns" value={stats?.active_campaigns || "0"} icon={IconCampaigns} />
          <StatCard label="Opted Out" value={stats?.opted_out || "0"} icon={IconOptout} />
        </div>

        {/* Charts + Quick Actions */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left — Charts */}
          <div className="flex-1 space-y-4 min-w-0">
            {/* Bar Chart */}
            <div className="card p-6" data-testid="messages-chart">
              <h3
                className="text-[15px] font-bold mb-0.5"
                style={{ color: "#555555", fontFamily: "Montserrat, sans-serif", letterSpacing: "0.1em" }}
              >
                MESSAGES SENT
              </h3>
              <p
                className="text-[11px] font-medium mb-5"
                style={{ color: "#555555", fontFamily: "Montserrat, sans-serif", letterSpacing: "0.05em" }}
              >
                {rangeLabel} overview
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} barGap={6} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F1F1" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#ABABAB", fontFamily: "Montserrat" }}
                    axisLine={{ stroke: "#E6E6E6" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#ABABAB", fontFamily: "Montserrat" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 11,
                      border: "1px solid #E6E6E6",
                      fontFamily: "Montserrat",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Bar dataKey="sent" name="Sent" shape={<RoundedBar />} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Line Chart */}
            <div className="card p-6" data-testid="rate-chart">
              <h3
                className="text-[15px] font-bold mb-0.5"
                style={{ color: "#555555", fontFamily: "Montserrat, sans-serif", letterSpacing: "0.1em" }}
              >
                PERFORMANCE TREND
              </h3>
              <p
                className="text-[11px] font-medium mb-5"
                style={{ color: "#555555", fontFamily: "Montserrat, sans-serif", letterSpacing: "0.05em" }}
              >
                {rangeLabel} — sent vs replies
              </p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F1F1" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: "#ABABAB", fontFamily: "Montserrat" }}
                    axisLine={{ stroke: "#E6E6E6" }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#ABABAB", fontFamily: "Montserrat" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 11,
                      border: "1px solid #E6E6E6",
                      fontFamily: "Montserrat",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                  />
                  <Line type="monotone" dataKey="sent" stroke="#FF5C35" strokeWidth={2.5} dot={false} name="Sent" />
                  <Line type="monotone" dataKey="replies" stroke="#B7B7B7" strokeWidth={2} dot={false} name="Replies" strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Right — Quick Actions */}
          <div className="flex flex-col gap-4 w-full lg:w-80 lg:shrink-0">
            <QuickAction
              to="/blast"
              title="SEND BLAST"
              desc="Text all or a segment of your contacts instantly"
              bgColor="#FF5C35"
              icon={IconBlast}
            />
            <QuickAction
              to="/campaigns/new"
              title="CREATE CAMPAIGN"
              desc="Set up automated drip sequences"
              bgColor="#172937"
              icon={IconCampaigns}
            />
            <QuickAction
              to="/contacts"
              title="VIEW CONTACTS"
              desc="Manage and filter your full contact list"
              bgColor="#676E73"
              icon={IconContacts}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
